import { EventEmitter } from 'events';
import rpointInPolygon from 'robust-point-in-polygon';
import { Box2, Box3, Object3D, Vector2, Vector2Tuple } from 'three';

import { toVector2 } from '@spazious/dollhouse-three';

import { FEnsembleBase } from '../fensembles/index.js';
import {
  Schema,
  SchemaFieldNumber,
  SchemaFieldPolygon,
  SchemaFields,
  SchemaFieldVector2,
  SchemaValues,
} from '../schema/index.js';
import { FInstanceEnsembleNode } from '../types/index.js';

const bbox = new Box3();

export type FArrangementEvents = {
  minsizeupdate: [];
};

export interface FArrangementBaseSchemaFields extends SchemaFields {
  orientation: SchemaFieldNumber;
  size: SchemaFieldVector2;
  mask: SchemaFieldPolygon;
}

export interface FArrangementBaseSchema extends Schema {
  fields: FArrangementBaseSchemaFields;
}

export type FArrangementBaseAttributes = Partial<SchemaValues<FArrangementBaseSchemaFields>>;

/**
 * Abstract base class for all layouts.
 * This class provides the basic structure and methods for managing layouts.
 */
export abstract class FArrangementBase<
  TSchema extends FArrangementBaseSchema,
  TValues extends Record<string, any> = {},
  TFCompositionSchema extends Schema = Schema,
  TFCompositionValues extends Record<string, any> = {}
> extends FEnsembleBase<TSchema, TValues & TFCompositionValues> {
  events: EventEmitter<FArrangementEvents> = new EventEmitter();
  usesEnsembles: boolean = false;
  ensembles: FInstanceEnsembleNode[] = [];

  constructor(
    schema: TSchema,
    initialValues?: Partial<FArrangementBaseAttributes & TValues & TFCompositionValues>,
    public fcomposition?: FEnsembleBase<TFCompositionSchema, TFCompositionValues>
  ) {
    super(schema, initialValues as any);
  }

  getNodeTypes(): number[] {
    if (!this.fcomposition) {
      throw new Error('FArrangementBase.getNodeTypes: fcomposition is not defined');
    }
    return this.fcomposition.getNodeTypes();
  }

  getInstanceBoundingBox(): Box3 {
    if (!this.fcomposition) {
      throw new Error('FArrangementBase.getInstanceBoundingBox: fcomposition is not defined');
    }
    if (this.fcomposition.boundingBox === null) {
      this.fcomposition.computeBoundingBox();
    }
    return bbox.makeEmpty().union(this.fcomposition.boundingBox!);
  }

  setObjects(objects: Object3D[]) {
    this.fcomposition?.setObjects(objects);
    const updated = super.setObjects(objects);
    if (updated) {
      this.events.emit('minsizeupdate');
    }
    return updated;
  }

  setValues(
    values: Partial<SchemaValues<TSchema['fields']> & TValues & TFCompositionValues>
  ): this {
    this.fcomposition?.setValues(values as any);
    return super.setValues(values);
  }

  updateValues(
    values: Partial<SchemaValues<TSchema['fields']> & TValues & TFCompositionValues>
  ): this {
    this.fcomposition?.updateValues(values as any);
    return super.updateValues(values);
  }

  getMaskPivot(mask: Vector2Tuple[][]): Vector2 {
    const [points] = mask;
    return new Box2()
      .setFromPoints(points.map((point) => toVector2(point)))
      .getCenter(new Vector2());
  }

  pointInPolygon(polygon: Vector2Tuple[][], point: Vector2Tuple): 0 | 1 | -1 {
    const [points, ...holes] = polygon;

    const parsedPoint = toVector2(point).toArray();

    const result = rpointInPolygon(points, parsedPoint);

    if (result === 1) {
      for (let i = 0; i < holes.length; i++) {
        const hole = holes[i];
        const holeResult = rpointInPolygon(hole, parsedPoint);
        if (holeResult < 1) {
          return (1 - holeResult) as 0 | 1 | -1;
        }
      }

      return 1;
    }

    return result;
  }

  getMinimumSize(): Vector2Tuple {
    throw new Error('FArrangementBase.getMinimumSize Method not implemented.');
  }

  /**
   * Check if polygonA contains polygonB inside of it.
   * Note: Holes are ignored
   */
  polygonContains(polygonA: Vector2Tuple[][], polygonB: Vector2Tuple[][]): boolean {
    const [pointsA] = polygonA;
    const [pointsB] = polygonB;

    return pointsB.every((point) => rpointInPolygon(pointsA, point) === -1);
  }
}
